<template>
  <div :class="$style.fraudReport" data-test="fraud-report">
    <div :class="$style.body">
      <h3>{{ t('fraudReport.banner.title') }}</h3>
      <div :class="$style.message" v-html="t('fraudReport.banner.message')" />
      <HgButton
        ref="fraudReportAction"
        :class="$style.button"
        icon="Error"
        theme="secondary-bordered"
        @click="onDialogOpen"
      >
        {{ t('fraudReport.banner.actionLabel') }}
      </HgButton>
    </div>
    <FraudReportModal
      v-if="isFraudReportModalLoaded"
      :isDialogOpen="isDialogOpen"
      :listing="listing"
      @dialogClose="onDialogClose"
      @formSubmitSuccess="onFormSubmitSuccess"
    />
  </div>
</template>

<script lang="ts" setup>
import { HgButton } from '@smg-real-estate/domus';
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { defineAsyncComponent, onMounted, ref } from 'vue';

import { InteractionContainerDetail } from '@/helpers/GTM/GTMParameters';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

defineOptions({
  name: 'FraudReportWidget',
});

const FraudReportModal = defineAsyncComponent(
  () => import('@/components/FraudReport/FraudReportModal.vue'),
);

interface Props {
  listing: Listing;
}

defineProps<Props>();

const emit = defineEmits<{
  reportButtonClick: [];
  formSubmitSuccess: [detail: InteractionContainerDetail];
}>();

const { t } = useI18n();

const route = useRoute();
const isDialogOpen = ref(false);
const isFraudReportModalLoaded = ref(false);

const onDialogClose = () => {
  isDialogOpen.value = false;
};

const onDialogOpen = () => {
  isDialogOpen.value = true;
  isFraudReportModalLoaded.value = true;
  emit('reportButtonClick');
};

const onFormSubmitSuccess = (containerDetail: InteractionContainerDetail) => {
  emit('formSubmitSuccess', containerDetail);
};

onMounted(() => {
  if (route.query?.reportFraud) {
    // Auto open the fraud modal for the native apps
    isDialogOpen.value = true;
    isFraudReportModalLoaded.value = true;
  }
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

$button-cta-min-width: 325px;

.fraudReport {
  display: block;

  .body {
    padding-bottom: $space-75;
    margin-top: 0;
    margin-bottom: $space-100;

    .button {
      justify-content: flex-start;
      width: 100%;
      margin-top: $space-50;

      @media (min-width: $viewport-medium-min) {
        width: auto;
        min-width: $button-cta-min-width;
      }
    }

    h3 {
      margin-top: 0;
      margin-bottom: $space-50;
    }

    .message {
      @include body-3;

      & {
        line-height: 1.75;
      }
    }
  }
}
</style>
